// Statistics → Parcels → Shipping states: where every shipped parcel is, from the carriers' own tracking
// (backend/tracking.py polls; backend/parcel_report.py decides what is listed and why a parcel is an
// exception — the CS connector's `parcels_in_state` runs the same code). Replaces Warehouse → Parcels
// (2026-09-04) in parcel tracking v2 (Claude, 2026-10-02): proper carrier names, the tracking code as a
// link the customer can be sent, the partner that delivers a Packeta parcel, a COD filter for the parcels
// waiting at a pickup point, and a Reason for every exception.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { PhoneCards, CardLine } from '../PhoneCards';
import { fmtWh } from '../time';
import { CARRIER_CODES, CARRIER_NAME, TrackLink } from '../carriers';

interface Mile { carrier?: string | null; number?: string | null; url?: string | null }
interface Parcel {
  id: number; carrier: string; carrier_name: string; kind?: string; state: string; state_text: string;
  order_id?: string; order_number?: string; webshop_id?: string; country?: string; delivery?: string;
  payment?: string; amount?: number; currency?: string; manual_label_id?: number; poll_note?: string;
  first_mile?: Mile; last_mile?: Mile | null; tracking_number: string;
  processed_at?: string; first_scan_at?: string; ready_for_pickup_at?: string; delivered_at?: string;
  expires_at?: string; last_event_at?: string; last_event_text?: string; reasons?: string[];
}
interface Ev {
  event_time: string; code: string; description?: string; location?: string; mapped_state?: string;
  text?: string; links?: { href: string; label: string }[];
}

type Tab = 'in_transit' | 'ready' | 'delivered' | 'exceptions';
const TABS: [Tab, string, string][] = [
  ['in_transit', 'In transit', 'Handed to the carrier and on the way — or waiting for its first scan.'],
  ['ready', 'Ready for pickup', 'At the pickup point or locker, waiting for the customer.'],
  ['delivered', 'Delivered', 'Collected or delivered in the last 14 days.'],
  ['exceptions', 'Exceptions', 'Parcels that need a person — each with its reason.'],
];

const STATE_STYLE: Record<string, string> = {
  created: 'bg-gray-800 text-gray-400', in_transit: 'bg-blue-900/50 text-blue-300',
  ready_for_pickup: 'bg-amber-900/40 text-amber-300', delivered: 'bg-green-900/40 text-green-300',
  returning: 'bg-orange-900/40 text-orange-300', returned: 'bg-red-900/40 text-red-300',
  cancelled: 'bg-gray-800 text-gray-500', exception: 'bg-red-900/40 text-red-300', unknown: 'bg-gray-800 text-gray-500',
};
const t = (s?: string) => (s ? fmtWh(s, 'datetime') : '—');
const money = (a?: number, c?: string) => a == null ? '' : `${a.toFixed(2)} ${c || ''}`.trim();

export default function ShippingStates({ webshops }: { webshops: { id: string; name: string }[] }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('in_transit');
  const [carrier, setCarrier] = useState('');
  const [store, setStore] = useState('');
  const [country, setCountry] = useState('');
  const [cod, setCod] = useState(false);
  const [q, setQ] = useState('');
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<number | null>(null);
  const [events, setEvents] = useState<Record<number, Ev[]>>({});
  const [polling, setPolling] = useState(false);
  const role = localStorage.getItem('wms_role') || 'staff';
  const storeName = (id?: string) => webshops.find(w => w.id === id)?.name || id || '';

  const filters = `carrier=${carrier}&store=${store}&country=${encodeURIComponent(country)}`;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/tracking/parcels?tab=${tab}&${filters}&cod=${cod}&q=${encodeURIComponent(q)}`);
      const d = await r.json();
      setParcels(r.ok ? (d.parcels || []) : []);
    } catch { setParcels([]); }
    setLoading(false);
  }, [tab, filters, cod, q]);
  const loadCounts = useCallback(() => {
    authFetch(`${API}/tracking/counts?${filters}`).then(async r => { if (r.ok) setCounts(await r.json()); }).catch(() => {});
  }, [filters]);

  useEffect(() => { const h = setTimeout(load, q ? 350 : 0); return () => clearTimeout(h); }, [load]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const toggle = async (id: number) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    if (!events[id]) {
      try {
        const r = await authFetch(`${API}/tracking/parcels/${id}/events`);
        const d = await r.json();
        setEvents(ev => ({ ...ev, [id]: r.ok ? (d.events || []) : [] }));
      } catch { /* the row stays open with no events */ }
    }
  };
  const pollNow = async () => {
    if (polling) return;
    setPolling(true);
    try {
      const r = await authFetch(`${API}/tracking/poll-now`, { method: 'POST', body: '{}' });
      if (r.ok) { await load(); loadCounts(); }
      else { const d = await r.json().catch(() => ({} as any)); alert(d.detail || 'Poll failed'); }
    } finally { setPolling(false); }
  };
  const openOrder = (p: Parcel) => { if (p.order_id) navigate(`/orders/new?open=${encodeURIComponent(p.order_id)}`); };

  const orderCell = (p: Parcel) => p.order_id
    ? <button onClick={e => { e.stopPropagation(); openOrder(p); }} className="text-white font-medium hover:underline decoration-dotted"
              title="Open the order">{p.order_number || p.order_id}</button>
    : <span className="text-gray-400">{p.manual_label_id ? 'manual label' : '—'}</span>;
  const pill = (p: Parcel) => (
    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${STATE_STYLE[p.state] || STATE_STYLE.unknown}`}>
      {p.state_text}</span>
  );
  const lastMile = (p: Parcel) => p.last_mile
    ? <span className="text-gray-400"><span className="text-gray-500">{p.last_mile.carrier || 'partner'}</span> <TrackLink num={p.last_mile.number} url={p.last_mile.url} /></span>
    : <span className="text-gray-700">—</span>;
  const eventsBlock = (p: Parcel) => !events[p.id] ? <span className="text-gray-500 text-sm">Loading events…</span>
    : events[p.id].length === 0 ? <span className="text-gray-500 text-sm">No carrier events yet{p.poll_note ? ` (${p.poll_note})` : ''}.</span>
    : (
      <div className="space-y-1">
        {events[p.id].map((e, i) => (
          <div key={i} className="flex gap-3 text-sm items-baseline">
            <span className="text-gray-500 whitespace-nowrap w-32 shrink-0">{t(e.event_time)}</span>
            <span className="text-gray-300 flex-1 min-w-0 break-words">
              {e.text || e.description || e.mapped_state || e.code}
              {(e.links || []).map((l, k) => (
                <a key={k} href={l.href} target="_blank" rel="noopener noreferrer"
                   className="ml-2 text-blue-400 hover:text-blue-300 whitespace-nowrap">↗ {l.label}</a>
              ))}
            </span>
            <span className="text-gray-600 whitespace-nowrap hidden sm:inline">{e.location || ''}</span>
          </div>
        ))}
      </div>
    );
  const sel = 'bg-gray-800 border border-gray-700 text-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500';
  const exc = tab === 'exceptions';

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(([k, label, hint]) => (
          <button key={k} onClick={() => setTab(k)} title={hint}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === k ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {label}{counts && counts[k] != null && <span className="ml-1.5 text-xs opacity-75 tabular-nums">{counts[k]}{counts[k] >= 500 ? '+' : ''}</span>}
          </button>
        ))}
        {(role === 'admin' || role === 'manager') && (
          <button onClick={pollNow} disabled={polling}
            className="ml-auto px-3 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-300 text-sm rounded-lg">
            {polling ? 'Polling carriers…' : '⟳ Poll now'}
          </button>
        )}
      </div>
      <p className="text-gray-500 text-xs mt-2">{TABS.find(x => x[0] === tab)?.[2]}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={carrier} onChange={e => setCarrier(e.target.value)} className={sel}>
          <option value="">All carriers</option>
          {CARRIER_CODES.map(c => <option key={c} value={c}>{CARRIER_NAME[c]}</option>)}
        </select>
        <select value={store} onChange={e => setStore(e.target.value)} className={sel}>
          <option value="">All stores</option>
          {webshops.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <input value={country} onChange={e => setCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="Country"
          className={`${sel} w-24`} title="Destination country, two letters (FI, SE …)" />
        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm cursor-pointer ${cod ? 'bg-amber-900/40 text-amber-200' : 'bg-gray-800 text-gray-400'}`}
               title="Cash-on-delivery parcels only — on 'Ready for pickup': the COD parcels waiting at a pickup point">
          <input type="checkbox" checked={cod} onChange={e => setCod(e.target.checked)} className="accent-amber-500" />
          COD only{counts?.ready_cod != null && <span className="text-xs opacity-75">({counts.ready_cod} waiting)</span>}
        </label>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search order # or any tracking code…"
          className={`${sel} w-full sm:w-72 sm:ml-auto`} />
      </div>

      <div className="mt-3 bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
        {loading ? <div className="p-6 text-gray-500 text-sm">Loading…</div>
          : parcels.length === 0 ? (
            <div className="p-6 text-gray-500 text-sm">{exc ? 'No exceptions — everything is moving.' : 'Nothing here.'}</div>
          ) : (
            <PhoneCards rows={parcels} keyOf={p => p.id}
              card={p => (
                <div onClick={() => toggle(p.id)}>
                  <div className="flex justify-between items-baseline gap-2">{orderCell(p)}{pill(p)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {p.carrier_name}{p.kind === 'return' ? ' · return' : ''}{p.country ? ` · ${p.country}` : ''} · <TrackLink num={p.first_mile?.number} url={p.first_mile?.url} />
                  </div>
                  {p.last_mile && <CardLine label="Delivered by">{lastMile(p)}</CardLine>}
                  {exc && (p.reasons || []).length > 0 && <CardLine label="Reason"><span className="text-amber-300">{p.reasons!.join(' · ')}</span></CardLine>}
                  <CardLine label="Processed">{t(p.processed_at)}</CardLine>
                  <CardLine label="Last event">{p.last_event_text ? `${p.last_event_text} (${t(p.last_event_at)})` : t(p.last_event_at)}</CardLine>
                  {p.expires_at && <CardLine label="Pickup deadline"><span className="text-amber-400">{t(p.expires_at)}</span></CardLine>}
                  {p.payment === 'cod' && <CardLine label="COD">{money(p.amount, p.currency)}</CardLine>}
                  {open === p.id && <div className="mt-2 border-t border-gray-800 pt-2">{eventsBlock(p)}</div>}
                </div>
              )}>
              <table className="w-full text-sm min-w-[1200px]">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="px-3 py-2.5 text-left">Order #</th>
                    <th className="px-3 py-2.5 text-left">Store · country</th>
                    <th className="px-3 py-2.5 text-left">Carrier · tracking</th>
                    <th className="px-3 py-2.5 text-left" title="Packeta: the partner carrier that delivers, with its own code">Last mile</th>
                    <th className="px-3 py-2.5 text-left">State</th>
                    {exc && <th className="px-3 py-2.5 text-left">Reason</th>}
                    <th className="px-3 py-2.5 text-left">Processed</th>
                    <th className="px-3 py-2.5 text-left">First scan</th>
                    {tab !== 'in_transit' && <th className="px-3 py-2.5 text-left">Ready</th>}
                    {tab === 'delivered' && <th className="px-3 py-2.5 text-left">Delivered</th>}
                    <th className="px-3 py-2.5 text-left">Last event</th>
                    {tab !== 'delivered' && <th className="px-3 py-2.5 text-left">Pickup deadline</th>}
                    <th className="px-3 py-2.5 text-right" title="Cash on delivery — the sum the carrier collects">COD</th>
                  </tr>
                </thead>
                <tbody>
                  {parcels.map(p => (
                    <React.Fragment key={p.id}>
                      <tr onClick={() => toggle(p.id)} className="border-b border-gray-800/60 hover:bg-gray-800/40 cursor-pointer align-top">
                        <td className="px-3 py-2">{orderCell(p)}</td>
                        <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{storeName(p.webshop_id)}{p.country ? ` · ${p.country}` : ''}</td>
                        <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                          {p.carrier_name}
                          {p.kind === 'return' && <span className="ml-1.5 text-[10px] uppercase px-1.5 py-0.5 rounded bg-gray-800 text-gray-400"
                            title="the customer's return parcel (Packeta return code)">return</span>}
                          <div className="text-xs"><TrackLink num={p.first_mile?.number} url={p.first_mile?.url} /></div>
                        </td>
                        <td className="px-3 py-2 text-xs">{lastMile(p)}</td>
                        <td className="px-3 py-2">{pill(p)}</td>
                        {exc && <td className="px-3 py-2 text-amber-300 text-xs">{(p.reasons || []).join(' · ')}</td>}
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{t(p.processed_at)}</td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{t(p.first_scan_at)}</td>
                        {tab !== 'in_transit' && <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{t(p.ready_for_pickup_at)}</td>}
                        {tab === 'delivered' && <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{t(p.delivered_at)}</td>}
                        <td className="px-3 py-2 text-gray-400 text-xs max-w-[260px]">
                          <div className="break-words">{p.last_event_text || ''}</div>
                          <div className="text-gray-600">{t(p.last_event_at)}</div>
                        </td>
                        {tab !== 'delivered' && <td className={`px-3 py-2 whitespace-nowrap ${p.expires_at ? 'text-amber-400' : 'text-gray-700'}`}>{t(p.expires_at)}</td>}
                        <td className="px-3 py-2 text-right text-gray-300 whitespace-nowrap tabular-nums">{p.payment === 'cod' ? money(p.amount, p.currency) : ''}</td>
                      </tr>
                      {open === p.id && (
                        <tr className="border-b border-gray-800/60 bg-gray-900/40">
                          <td colSpan={13} className="px-6 py-3">{eventsBlock(p)}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </PhoneCards>
          )}
      </div>
      <p className="text-gray-600 text-xs mt-2">
        Times are Estonian time. Labels bought ahead for orders still in the warehouse, and replaced labels that never
        shipped, are not listed. Click a row for the carrier's events; the order number opens the order.
      </p>
    </div>
  );
}
